import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { usePost, Board as BoardType } from '../../contexts/PostContext';
import PostList from '../../components/PostList';
import { boardAPI } from '../../api';

// 스타일드 컴포넌트
const BoardDetailContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0.5rem 0;
`;

const RightContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const BoardContainer = styled.div`
  width: 100%;
  padding: 20px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    padding: 15px 10px;
    border-radius: 0;
    box-shadow: none;
    background-color: #f9f9f9;
  }
`;

const BoardTitle = styled.h1`
  font-size: 24px;
  color: #333;
  margin-bottom: 10px;
  padding-bottom: 15px;
  border-bottom: 2px solid #e0e0e0;
  
  @media (max-width: 768px) {
    font-size: 20px;
    padding-bottom: 10px;
    color: #4a90e2;
    text-align: left;
    border-bottom: 2px solid #4a90e2;
  }
`;

const BoardDescription = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 20px;
  line-height: 1.5;
  
  @media (max-width: 768px) {
    font-size: 13px;
    margin-bottom: 15px;
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  width: 100%;
`;

const ErrorMessage = styled.div`
  padding: 15px;
  margin: 15px 0;
  background-color: #f8d7da;
  color: #721c24;
  border-radius: 4px;
`;

const BoardDetail: React.FC = () => {
  const { boardId } = useParams<{ boardId: string }>();
  const { isAuthenticated, user } = useAuth();
  const [searchType, setSearchType] = useState('1');
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  
  // 게시물 컨텍스트
  const {
    getList,
    error,
    fetchPostListByBoard
  } = usePost();

  const { list, pagination } = getList(Number(boardId));

  // 상태 추가
  const [board, setBoard] = useState<BoardType | null>(null);
  const [canWrite, setCanWrite] = useState<boolean>(false);

  // 게시판 정보와 게시물 목록 로드
  useEffect(() => {
    if (!boardId) return;

    const loadBoardAndPosts = async () => {
      try {
        setLoading(true);

        // 게시판 데이터 가져오기
        const [boardResponse] = await Promise.all([
          boardAPI.getBoardById(Number(boardId)),
          fetchPostListByBoard(Number(boardId), 1, 20)
        ]);

        const boardData = boardResponse.data.board;
        setBoard({
          id: Number(boardId),
          name: boardData.name,
          description: boardData.description
        });

        if (isAuthenticated && boardData.permissions) {
          const userData = user as any;
          const userRoleId = userData?.roleId; // 기본값은 게스트 역할

          const permission = boardData.permissions.find((p: any) => p.role_id === userRoleId);
          const hasWritePermission = permission ? permission.can_write === 1 : false;

          setCanWrite(userRoleId === 4 || hasWritePermission);
        } else {
          setCanWrite(false);
        }
      } catch (error) {
        console.error('게시판 데이터 로딩 오류:', error);
      }

      setLoading(false);
    };

    loadBoardAndPosts();
  }, [boardId, fetchPostListByBoard, isAuthenticated, user]);

  const handlePageChange = useCallback((page: number) => {
    if (boardId) {
      fetchPostListByBoard(Number(boardId), page, 20);
    }
  }, [boardId, fetchPostListByBoard]);

  const handleSearch = useCallback((type: string, query: string) => {
    if (boardId) {
      setLoading(true);
      fetchPostListByBoard(Number(boardId), 1, 20, Number(type), query).then(() => {
        setLoading(false);
      });
    }
  }, [boardId, fetchPostListByBoard]);

  if ((loading && !list.length) || !board) {
    return (
      <BoardDetailContainer>
        <RightContent>
          <LoadingWrapper>데이터를 불러오는 중입니다...</LoadingWrapper>
        </RightContent>
      </BoardDetailContainer>
    );
  }

  if (error) {
    return (
      <BoardDetailContainer>
        <RightContent>
          <ErrorMessage>오류가 발생했습니다: {error}</ErrorMessage>
        </RightContent>
      </BoardDetailContainer>
    );
  }

  return (
    <BoardDetailContainer>
      <RightContent>
        <BoardContainer>
          <BoardTitle>{board.name}</BoardTitle>
          {board.description && <BoardDescription>{board.description}</BoardDescription>}

          <PostList 
            list={list}
            pagination={pagination}
            loading={loading}
            customPageHandler={handlePageChange}
            boardId={Number(boardId)}
            title={board.name}
            canWrite={canWrite}
            showSearch={true}
            searchHandler={handleSearch}
            showWriteButton={true}
          />
        </BoardContainer>
      </RightContent>
    </BoardDetailContainer>
  );
};

export default BoardDetail; 